import {
  AssessmentEvent,
  AssessmentEventbyRaterUserQuery,
  CompleteAssessmentRequestMutation,
  CompleteAssessmentRequestMutationVariables,
  CompletedAssessmentEvent,
  ModelAssessmentEventConnection,
  ReasonForDecline,
  SubmittedAnswer,
  UpdateAssessmentEventInput,
  UpdateAssessmentEventMutation,
  UpdateAssessmentEventMutationVariables
} from "../../API";
import * as queries from "../../graphql/queries";
import * as mutations from "../../graphql/mutations";
import { genericQuery, listQueryAutoPaginated } from "../api/apiUtils";
import { API } from "aws-amplify";

export const fetchAssessmentEventsByRaterUserId = async (
  raterUserId: string
) => {
  const predicate = { raterUserId: raterUserId };
  return await listQueryAutoPaginated<
    AssessmentEventbyRaterUserQuery,
    AssessmentEvent,
    ModelAssessmentEventConnection
  >(
    queries.assessmentEventbyRaterUser,
    "assessmentEventbyRaterUser",
    "items",
    predicate
  );
};

export type PendingAssessmentResponse = {
  activityEventId: string;
  activityEventDate: string | null;
  activityEventNotes: string | null;
  user: {
    firstName: string | null;
    lastName: string | null;
    suffix: string | null;
    fullName: string;
    initials: string;
  };
  assessments: AssessmentEventDetails[];
};

export type AssessmentEventDetails = {
  sponsor: {
    sponsorId: string;
    sponsorName: string;
    sponsorLogoUrl: string | null;
  };
  assessmentEventId: string;
  activityTitle: string;
  assessmentEventSubjectUserId: string | null;
  assessmentCreationDate: string;
  assessmentEventExpirationDate: string | null;
};

export type FlattenedAssessmentDetails = {
  assessmentEventId: string;
  assessmentEventSubjectUserId: string | null;
  assessmentEventCreationDate: string;
  assessmentEventExpirationDate: string | null;
  sponsorBranding: {
    sponsorId: string;
    sponsorName: string;
    sponsorLogoUrl: string | null;
  };
  activityEventId: string;
  activityTitle: string;
  activityEventDate: string | null;
  activityEventNotes: string | null;
  user: {
    firstName: string | null;
    lastName: string | null;
    fullName: string;
    initials: string;
  };
};

export const fetchPendingAssessmentsByRater = async (userId: string) => {
  const path = `user/${userId}/pending-assessments`;
  const pendingAssessments = (await API.get(
    "simpl-api",
    path,
    {}
  )) as PendingAssessmentResponse[];
  return pendingAssessments;
};

export const updateAssessmentEvent = async (
  input: UpdateAssessmentEventInput
) => {
  const variables: UpdateAssessmentEventMutationVariables = {
    input: input
  };
  return await genericQuery<UpdateAssessmentEventMutation, AssessmentEvent>(
    mutations.updateAssessmentEvent,
    "updateAssessmentEvent",
    variables
  );
};

type CompleteAssesessmentRequestInput = {
  assessmentEventId: string;
  answers: Array<SubmittedAnswer>;
};

type ActivityProcedure = {
  activityId: string;
  activityTitle: string;
  acgmeProcedureId: string;
  acgmeProcedureName: string;
  acgmeCode: string;
};

export type AcgmeProcedureData = {
  acgmeEnabled: boolean;
  activityProcedureData: ActivityProcedure[];
};

export const completeAssessmentRequest = async (
  assessmentEventId: string,
  answers: SubmittedAnswer[]
) => {
  const input: CompleteAssesessmentRequestInput = {
    assessmentEventId: assessmentEventId,
    answers: answers
  };
  const id = assessmentEventId;
  const path = `assessmentEvent/${id}/complete`;
  const acgmeProcedureData = (await API.post("simpl-api", path, {
    body: {
      ...input
    }
  })) as AcgmeProcedureData;
  return acgmeProcedureData;
};

export type DeclineAssessmentResponse = {
  message: string;
  status: string;
};

export const declineAssessmentRequest = async (
  id: string,
  reason: ReasonForDecline
) => {
  const path = `assessmentEvent/${id}/decline-assessment`;
  const declineAssessment = (await API.post("simpl-api", path, {
    body: {
      reason
    }
  })) as DeclineAssessmentResponse;
  return declineAssessment;
};

export enum InputType {
  Radio = "Radio",
  Feedback = "Feedback",
  Checkbox = "Checkbox",
  Paragraph = "Paragraph",
  ShortAnswer = "ShortAnswer",
  Dropdown = "Dropdown",
  LinearScale = "LinearScale",
  DateTime = "DateTime",
  Attachment = "Attachment",
  NoInput = "NoInput"
}

export type AnswerOption = {
  id: string;
  anchor?: string | null;
  sortingIndex?: number | null;
  helpText?: string | null;
  value?: string | null;
  questionId: string;
  createdAt: string;
  updatedAt: string;
};

export type QuestionAnswerOption = {
  id: string;
  title: string;
  sortingIndex: number;
  helpText: string;
  inputTypeName: InputType;
  prompt: string;
  required: boolean;
  answerOptions: AnswerOption[];
};

export type AssessmentDetails = {
  questions: QuestionAnswerOption[];
  user: {
    firstName: string;
    lastName: string;
    fullName: string;
    initials: string;
  };
  loggedInUserRole: string;
  activityTitle: string;
  activityEventNotes: string;
  creatorId: string;
  assessmentExpirationDurationHours: number;
  assessmentEventExpirationDate: string;
  showFeedbackQuestionToTrainees: boolean;
  assessmentEventId: string;
  assessmentEventCreationDate: string;
  assessmentEventCompletionDate: string;
  assessmentEventDeclined: string;
  assessmentEventSubjectUserId: string;
  assessmentEventRaterUserId: string;
  sponsorBranding: {
    sponsorId: string;
    sponsorName: string;
    sponsorLogoUrl: string;
  };
}

export type AssessmentEventInput = {
  assessmentEventId: string;
};

export const fetchAssessmentEvent = async (assessmentEventId: string) => {
  const path = `assessment/${assessmentEventId}/completion-details`;
  const assessmentDetails = (await API.get(
    "simpl-api",
    path,
    {}
  )) as AssessmentDetails;
  return assessmentDetails;
};
